import React from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';

// context
import { useListingContext } from '@/context/listing';

// hooks
import useListingLoad from '@/hooks/listing/useListingLoad';
import useFetchListingData from '@/hooks/listing/useFetchListingData';
import useListingPopstate from '@/hooks/listing/useListingPopstate';
import useListingPagination from '@/hooks/listing/useListingPagination';
import useKenwoodToggleView from '@/hooks/listing/Kenwood/useKenwoodToggleView';
import useLockUnlockBody from '@/hooks/useLockUnlockBody';

// components
import FiltersHead from '@/kenwood/components/atoms/FiltersHead';
import FiltersBarMobile from '@/kenwood/components/molecules/FiltersBarMobile';
import FiltersPopupMobile from '@/kenwood/components/molecules/FiltersPopupMobile';
import AppliedFilters from '@/kenwood/components/molecules/AppliedFilters';
import FiltersBarDesktop from '@/kenwood/components/molecules/FiltersBarDesktop';

const Filters = ({ categoryCode, categoryName, sortData, hasLoadMore }) => {
  // hooks
  const { t } = useTranslation();
  const { isGrid, toggleView } = useKenwoodToggleView();
  const [
    { areMobileFiltersOpen, filtersData },
    { toggleMobileFilters, clearFilters, pushQuery, setQuerySort },
  ] = useListingContext();
  const handleToggleMobileFilters = useLockUnlockBody(
    !areMobileFiltersOpen,
    toggleMobileFilters,
  );
  useListingLoad({ categoryCode, sortData, hasLoadMore });
  useFetchListingData(categoryCode);
  useListingPopstate();
  useListingPagination();

  return (
    <>
      <FiltersHead categoryName={categoryName} count={filtersData.count} />
      <FiltersBarMobile
        isGrid={isGrid}
        filtersLabel={t('listing:listing.filters.mobile.title')}
        viewLabel={t('listing:listing.filters.mobile.view.label')}
        onToggleView={toggleView}
        onOpenMobileFilters={handleToggleMobileFilters}
      />
      <FiltersPopupMobile
        areMobileFiltersOpen={areMobileFiltersOpen}
        filtersData={filtersData}
        filterLabel={t('listing:listing.filters.mobile.title')}
        clearFiltersLabel={t('listing:listing.filters.mobile.clear.all.label')}
        applyLabel={t('listing:listing.filters.mobile.apply.label')}
        sortByLabel={t('listing:listing.filters.sort.by.label')}
        onClose={handleToggleMobileFilters}
        onClearFilters={clearFilters}
        onApplyFilter={pushQuery}
        onApplySort={setQuerySort}
      />
      <FiltersBarDesktop
        filtersData={filtersData}
        filterByLabel={t('listing:listing.filters.filter.by.label')}
        sortByLabel={t('listing:listing.filters.sort.by.label')}
        applyLabel={t('listing:listing.filters.mobile.apply.label')}
        onApplyFilter={pushQuery}
        onApplySort={setQuerySort}
      />
      <AppliedFilters
        breadcrumbs={filtersData.breadcrumbs}
        clearFiltersLabel={t('listing:listing.filters.mobile.clear.all.label')}
        onClearFilters={clearFilters}
        onRemoveFilter={pushQuery}
      />
    </>
  );
};

Filters.propTypes = {
  categoryCode: PropTypes.string.isRequired,
  categoryName: PropTypes.string.isRequired,
  sortData: PropTypes.string.isRequired,
  hasLoadMore: PropTypes.string.isRequired,
};

Filters.defaultProps = {};

export default Filters;
